import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { NewCaseModal } from '../components/case-management/NewCaseModal'
import { detectDashboardTheme } from './org-cases-page/org-cases-page.helpers'
import {
  EmptyState,
  ErrorState,
  LoadingState,
  OwnerSection,
  PageHeader,
  SearchBar,
} from './org-cases-page/org-cases-page.parts'
import { useOrgCasesPage } from './org-cases-page/use-org-cases-page'

export function OrgCasesPage(): ReactElement {
  const { t } = useTranslation('cases')
  const theme = detectDashboardTheme()
  const state = useOrgCasesPage({ t })

  if (state.loading) return <LoadingState t={t} />
  if (state.error)
    return (
      <ErrorState
        message={state.error}
        onRetry={() => {
          void state.fetchData()
        }}
        t={t}
      />
    )

  return (
    <div className="space-y-6">
      <PageHeader
        totalCount={state.data?.totalCount ?? 0}
        ownersCount={state.data?.byOwner.length ?? 0}
        loading={state.loading}
        canCreateCase={Boolean(state.quota?.canCreateCase)}
        onRefresh={() => {
          void state.fetchData()
        }}
        onNewCase={() => {
          state.setIsNewCaseModalOpen(true)
        }}
        t={t}
      />
      <SearchBar value={state.searchQuery} onChange={state.setSearchQuery} t={t} />
      <div className="space-y-4">
        {state.filteredOwners.length === 0 ? (
          <EmptyState hasSearch={Boolean(state.searchQuery)} t={t} />
        ) : (
          state.filteredOwners.map((owner) => (
            <OwnerSection
              key={owner.ownerId}
              owner={owner}
              expanded={state.expandedOwners.has(owner.ownerId)}
              onToggle={() => {
                state.toggleOwner(owner.ownerId)
              }}
              onCaseClick={state.handleCaseClick}
              theme={theme}
            />
          ))
        )}
      </div>
      <NewCaseModal
        isOpen={state.isNewCaseModalOpen}
        clients={state.clients}
        quotaStatus={state.quota}
        isCreating={state.creating}
        isCreatingClient={state.creatingClient}
        onSubmit={(input) => {
          void state.handleCreateCase(input)
        }}
        onCreateClient={state.handleCreateClient}
        onClose={() => {
          state.setIsNewCaseModalOpen(false)
        }}
      />
    </div>
  )
}
